// Lib
import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import delay from '../../../../common/utils/lib/delay';

// Utils
import { animateScroll } from '../../../utils/animation/animateScrollIntoView';
import getSelectionRect from '../../../utils/dom/getSelectionRect';
import getParentScrollableSection from '../../../utils/dom/getParentScrollableSection';
import getTextSelectionFocusElement from '../../../components/editor/domUtils/getTextSelectionFocusElement';

// Constants
import { PREVENT_SCROLL_TO_ELEMENT_EDITOR_KEYS } from './mobileEditingMiddleware';

// Selectors
import {
    getCurrentlyEditingEditorKey,
    getCurrentlyEditingId,
} from '../../../element/selection/currentlyEditingSelector';

const TOP_PADDING = 30;
const BOTTOM_PADDING = 50;
const HORIZONTAL_PADDING = 50;

const NO_SCROLL = { x: 0, y: 0 };

const TOOLBAR_TRANSITION_DELAY = 120;

/**
 * Returns a scroll delta if we're near the vertical extremities of the scrollable parent.
 */
const getScrollableParentVerticalScrollDelta = (
    selectionClientRect: DOMRect,
    scrollableParentRect: DOMRect,
): number => {
    const topScroll = selectionClientRect.top - TOP_PADDING - scrollableParentRect.top;
    if (topScroll < 0) return topScroll;

    const bottomScroll = selectionClientRect.bottom + BOTTOM_PADDING - scrollableParentRect.bottom;
    if (bottomScroll > 0) return bottomScroll;

    return 0;
};

/**
 * Returns a scroll delta if we're near the horizontal extremities of the scrollable parent.
 */
const getScrollableParentHorizontalScrollDelta = (
    selectionClientRect: DOMRect,
    scrollableParentRect: DOMRect,
): number => {
    const leftEdgeScroll = selectionClientRect.left - HORIZONTAL_PADDING - scrollableParentRect.left;
    if (leftEdgeScroll < 0) return leftEdgeScroll;

    const rightEdgeScroll = selectionClientRect.right + HORIZONTAL_PADDING - scrollableParentRect.right;
    if (rightEdgeScroll > 0) return rightEdgeScroll;

    return 0;
};

/**
 * Returns a scroll delta if the caret gets close to the toolbar's top.
 */
const getToolbarVerticalScrollDelta = (selectionClientRect: DOMRect, toolbarClientRect: DOMRect): number => {
    const bottomScroll = selectionClientRect.bottom + BOTTOM_PADDING - toolbarClientRect.top;
    if (bottomScroll > 0) return bottomScroll;

    return 0;
};

/**
 * Determines an amount to scroll the scrollable parent by if we're near the edges.
 */
const getScrollDelta = (
    selectionClientRect: DOMRect,
    toolbarClientRect: DOMRect,
    scrollableParentRect: DOMRect,
): { x: number; y: number } => {
    // If the caret needs scrolling from the bottom, the toolbar delta will always be bigger than the scrollable parent delta.
    // If the caret needs scrolling from the top, the toolbar delta will always be 0.
    const verticalDelta =
        getToolbarVerticalScrollDelta(selectionClientRect, toolbarClientRect) ||
        getScrollableParentVerticalScrollDelta(selectionClientRect, scrollableParentRect);

    const scrollDelta = {
        x: getScrollableParentHorizontalScrollDelta(selectionClientRect, scrollableParentRect),
        y: verticalDelta,
    };

    if (scrollDelta.x !== 0 || scrollDelta.y !== 0) {
        return scrollDelta;
    }

    return NO_SCROLL;
};

/**
 * Scrolls the scrollable parent (e.g. canvas or quick notes) to ensure that the caret
 * is visible when editing an element.
 */
const useScrollToCaretOnEdit = (toolbarRef: React.RefObject<HTMLDivElement>) => {
    const currentlyEditingId = useSelector(getCurrentlyEditingId);
    const currentlyEditingEditorKey = useSelector(getCurrentlyEditingEditorKey);
    const shouldPreventScroll = useRef<boolean>(false);

    useEffect(() => {
        if (!currentlyEditingId) return;
        let animationCancelFn: (() => void) | undefined;

        const allowScrollToCaretOnStart = PREVENT_SCROLL_TO_ELEMENT_EDITOR_KEYS.includes(currentlyEditingEditorKey);

        // Skip scrolling to caret in the first second of starting editing. This is because we are also
        // animating scrolling to most elements in mobileEditingMiddleware.
        // Having 2 scroll animations causes screen to bobble on edit start
        shouldPreventScroll.current = true;
        setTimeout(() => {
            shouldPreventScroll.current = false;
        }, 1000);

        const onSelectionChange = () => {
            if (shouldPreventScroll.current) return;

            // TODO-MOBILE - For now only scroll if the selection is a caret. When it's a range it's much
            //  more difficult to determine the caret's position
            const selection = window.getSelection();
            if (selection?.type !== 'Caret') return;

            // NOTE: This isn't ideal as if the selection is not collapsed, it will be
            //  a range spanning the full selection.
            //  Ideally we'd want a range that is just the anchor position.
            const selectionClientRect = getSelectionRect() as DOMRect;
            const selectionElement = getTextSelectionFocusElement();
            const scrollableParent = getParentScrollableSection(selectionElement);

            if (!scrollableParent) return;
            if (!toolbarRef.current) return;
            if (!selectionClientRect) return;

            const scrollableParentRect = scrollableParent.getBoundingClientRect();

            if (!scrollableParentRect) return;
            if (selectionClientRect.top < scrollableParentRect.top) return;

            const toolbarClientRect = toolbarRef.current.getBoundingClientRect();

            const scrollDelta = getScrollDelta(selectionClientRect, toolbarClientRect, scrollableParentRect);

            const shouldScroll = scrollDelta.x !== 0 || scrollDelta.y !== 0;
            if (!shouldScroll) return;

            if (animationCancelFn) animationCancelFn();

            animationCancelFn = animateScroll({
                scrollableParent,
                fromScrollLeft: scrollableParent.scrollLeft,
                fromScrollTop: scrollableParent.scrollTop,
                toScrollLeft: scrollableParent.scrollLeft + scrollDelta.x,
                toScrollTop: scrollableParent.scrollTop + scrollDelta.y,
                interpolationFactor: 0.2,
            });
        };

        // For elements that allow scroll to caret on start, we want to scroll when
        // the toolbar is in position so that we can calculate the scroll correctly
        const onKeyboardDidShow = () => {
            if (!allowScrollToCaretOnStart) return;

            shouldPreventScroll.current = false;
            delay(TOOLBAR_TRANSITION_DELAY).then(onSelectionChange);
        };

        document.addEventListener('selectionchange', onSelectionChange);
        window.addEventListener('keyboardDidShow', onKeyboardDidShow);

        return () => {
            shouldPreventScroll.current = false;
            document.removeEventListener('selectionchange', onSelectionChange);
            window.removeEventListener('keyboardDidShow', onKeyboardDidShow);
        };
    }, [currentlyEditingId, currentlyEditingEditorKey]);
};

export default useScrollToCaretOnEdit;
